import { useRuntimeConfig } from '#imports'

import { YOUTUBE_VIDEO_TITLE } from '~/scopes/product-core/components/presentationals/GalleryNew/components/YoutubeVideo/YoutubeVideo.constants'

import { DEFAULT_MARKETING_VIDEO } from '../MarketingVideo.constants'
import type { VideoSource } from '../MarketingVideo.types'
import {
  resolveVideoPoster,
  resolveVideoSources,
} from '../MarketingVideo.utils'

export type GalleryVideoSource = {
  isSelfHosted: boolean
  sources: VideoSource[]
  poster: string
  title: string
}

/**
 * Single source of truth for the PDP gallery video.
 *
 * Decides whether to serve the self-hosted `<video>` (Cloudflare R2) or the
 * legacy YouTube embed, based on the `FF_PRODUCT_PAGE_SELF_HOSTED_VIDEO_ENABLED`
 * feature flag, and resolves the matching sources/poster/title.
 *
 * Every gallery render site and data composable calls this so the
 * YouTube-vs-self-hosted decision (and the poster/title shown in the carousel
 * cell) can't drift between surfaces. Reads only SSR-stable runtime config, so
 * it is hydration-safe and idempotent.
 */
export function useGalleryVideoSource(): GalleryVideoSource {
  const config = useRuntimeConfig()
  const isSelfHosted =
    config.public.FF_PRODUCT_PAGE_SELF_HOSTED_VIDEO_ENABLED === true

  return {
    isSelfHosted,
    sources: isSelfHosted ? resolveVideoSources(DEFAULT_MARKETING_VIDEO) : [],
    // The poster is the same bundled static image for both paths, so the
    // carousel cell never flashes a different thumbnail when the flag flips.
    poster: resolveVideoPoster(DEFAULT_MARKETING_VIDEO),
    title: isSelfHosted ? DEFAULT_MARKETING_VIDEO.title : YOUTUBE_VIDEO_TITLE,
  }
}
